'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCheckoutStore } from '../../store/checkoutStore';

interface HashFormValues {
  txHash: string;
}

// BTC txid regex: 64 hex characters
const BTC_TXID_REGEX = /^[a-fA-F0-9]{64}$/;

const TELEGRAM_LINK = 'https://t.me/x_luminary';

export default function AwaitingHashStep() {
  const { setStep, setTxHash } = useCheckoutStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<HashFormValues>({ mode: 'onChange' });

  const hashValue = watch('txHash', '');

  const onSubmit = (data: HashFormValues) => {
    setIsSubmitting(true);
    setTxHash(data.txHash.trim());
    // Redirect directly to Telegram
    window.location.href = TELEGRAM_LINK;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 animate-slide-up">
      {/* Instruction */}
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-bold text-foreground">Paste your transaction hash</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          After sending BTC, find your transaction ID (TXID) in your wallet or on any Bitcoin block explorer (e.g. mempool.space, blockstream.info). It&apos;s a 64-character string.
        </p>
      </div>

      {/* Notice */}
      <div className="rounded-xl p-4 flex items-start gap-3"
        style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
        <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-foreground font-semibold">Your file will be shared after payment confirmation.</span>{' '}
          Once you submit your transaction hash, you&apos;ll be redirected to our Telegram where the file will be delivered upon verification.
        </p>
      </div>

      {/* Hash input */}
      <div className="flex flex-col gap-2">
        <label htmlFor="txHash" className="text-sm font-semibold text-foreground">
          Transaction Hash (TXID)
          <span className="text-red-400 ml-1">*</span>
        </label>
        <p className="text-xs text-muted-foreground">
          64 hexadecimal characters — starts with letters and numbers (e.g. 4a5e1e4b...)
        </p>
        <textarea
          id="txHash"
          rows={3}
          placeholder="4a5e1e4baab89f3a32518a88c31bc87f618f76673e2cc77ab2127b7afdeda33b"
          className={`input-field resize-none ${errors.txHash ? 'input-error' : ''}`}
          style={{ fontFamily: 'var(--font-mono)' }}
          {...register('txHash', {
            required: 'Transaction hash is required',
            pattern: {
              value: BTC_TXID_REGEX,
              message: 'Invalid format — must be exactly 64 hexadecimal characters',
            },
            validate: {
              noSpaces: (v) => v.trim() === v || 'Remove any spaces before or after the hash',
            },
          })}
        />
        {errors.txHash && (
          <p className="text-xs text-red-400 flex items-center gap-1.5" role="alert">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.txHash.message}
          </p>
        )}
        {/* Character count */}
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            {hashValue.trim().length > 0 && (
              <span className={hashValue.trim().length === 64 ? 'text-green-400' : 'text-muted-foreground'}>
                {hashValue.trim().length}/64 characters
              </span>
            )}
          </span>
          {hashValue.trim().length === 64 && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Valid format
            </span>
          )}
        </div>
      </div>

      {/* Where to find hash */}
      <div className="rounded-xl p-4 flex flex-col gap-3"
        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Where to find your TX hash</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            { name: 'mempool.space', desc: 'Search your address' },
            { name: 'blockstream.info', desc: 'BTC explorer' },
            { name: 'Your wallet app', desc: 'Transaction details' },
          ].map((src) => (
            <div key={`src-${src.name}`} className="rounded-lg p-2.5 text-center"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold text-foreground">{src.name}</p>
              <p className="text-xs text-muted-foreground">{src.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => useCheckoutStore.getState().setStep('payment_instructions')}
          className="btn-secondary flex-1 py-3.5 text-sm font-semibold flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to address
        </button>
        <button
          type="submit"
          disabled={isSubmitting || hashValue.trim().length !== 64}
          className="btn-primary flex-[2] py-3.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
        >
          {isSubmitting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Redirecting...
            </>
          ) : (
            <>
              Submit & Go to Telegram
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </>
          )}
        </button>
      </div>
    </form>
  );
}